/**
 * Fractal lightning geometry.
 *
 * A real lightning channel is not a zigzag. It is a rough path that stays rough
 * at every scale, it wanders further than you expect, it grows thinner toward
 * the ground, and it throws off branches that fork again and die out. Drawing
 * that by hand gives a lightning BOLT SYMBOL, which is what the stylised
 * `lightning` effect is. This module generates the real shape instead, by
 * midpoint displacement: take a segment, push its midpoint sideways by an
 * amount proportional to its own length, repeat on both halves. The result is
 * self similar, which is exactly why it reads as lightning.
 *
 * Everything here is DETERMINISTIC. The seed is fixed by the caller and the
 * random source is a small LCG, never `Math.random`, because the generated path
 * ends up inside a CSS string that is rendered on the server and again in the
 * browser: a different path on each side would be a hydration mismatch, and a
 * different path on each import would make every snapshot and diff unstable.
 *
 * React-free and dependency-free. See `lightningPath.test.ts` for the
 * invariants that matter.
 */

/** A point in the bolt's own 300 x 500 coordinate space. */
interface Pt {
  x: number;
  y: number;
}

/** One drawn stroke of the bolt. `w` is a RELATIVE width (1 = the trunk at its
 *  thickest), so the same geometry can be stamped at any absolute weight - the
 *  thin white core and the wide coloured halo are the same paths twice. */
export interface BoltStroke {
  d: string;
  w: number;
}

export const BOLT_W = 300;
export const BOLT_H = 500;

/** Linear congruential generator - the numbers are the Numerical Recipes pair.
 *  Small, seeded and reproducible, which is the whole requirement here. */
const lcg = (seed: number): (() => number) => {
  let s = (seed >>> 0) || 1;
  return () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
};

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

const round1 = (n: number): number => Math.round(n * 10) / 10;

const toPath = (pts: Pt[]): string => "M" + pts.map((p) => `${round1(p.x)} ${round1(p.y)}`).join("L");

/** How close to the edge of the box a displaced point may land. */
const EDGE = 10;

/**
 * One pass of midpoint displacement over a polyline. The offset is a fraction
 * of the SEGMENT's own length, not a fixed number of units, which is what keeps
 * the roughness self similar as the segments get shorter.
 *
 * The new point is kept inside the box. Not for tidiness: the SVG clips at its
 * viewBox, so a channel that wandered out would come back with a flat cut
 * across it, and a flat edge is the one thing lightning never has. A clamp only
 * bites on the occasional outlier, and the next pass displaces away from the
 * clamped point, so the channel turns back inward rather than sliding along the
 * edge.
 */
function displaceOnce(pts: Pt[], rand: () => number, wobble: number, minLen: number): Pt[] {
  const out: Pt[] = [pts[0]!];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len > minLen) {
      const off = (rand() * 2 - 1) * wobble * len;
      // Perpendicular to the segment is (-dy, dx) / len.
      out.push({
        x: clamp((a.x + b.x) / 2 - (dy / len) * off, EDGE, BOLT_W - EDGE),
        y: clamp((a.y + b.y) / 2 + (dx / len) * off, 0, BOLT_H),
      });
    }
    out.push(b);
  }
  return out;
}

const roughen = (pts: Pt[], rand: () => number, wobble: number, passes: number, minLen: number): Pt[] => {
  let cur = pts;
  for (let i = 0; i < passes; i++) cur = displaceOnce(cur, rand, wobble, minLen);
  return cur;
};

/**
 * The complete bolt: a tapering trunk plus branches, and a few branches that
 * fork again. Returned widest first so a renderer can stamp them in order.
 */
export function boltStrokes(seed: number): BoltStroke[] {
  const rand = lcg(seed);
  const strokes: BoltStroke[] = [];

  // The trunk. It starts near the top centre and drifts sideways on the way
  // down, because a channel that falls straight reads as a crack, not a bolt.
  const trunk = roughen(
    [
      { x: BOLT_W * 0.52, y: 0 },
      { x: BOLT_W * (0.26 + rand() * 0.46), y: BOLT_H },
    ],
    rand,
    0.21,
    7,
    5,
  );

  // Taper: the return stroke is brightest and widest at the top of the channel
  // and thins toward the ground. Four bands is enough to read as a taper
  // without four visible steps.
  const cuts = [0, 0.36, 0.64, 0.85, 1];
  for (let i = 0; i < cuts.length - 1; i++) {
    const from = Math.round(cuts[i]! * (trunk.length - 1));
    const to = Math.round(cuts[i + 1]! * (trunk.length - 1));
    strokes.push({ d: toPath(trunk.slice(from, to + 1)), w: 1 - i * 0.18 });
  }

  // Branches leave the trunk at a shallow downward angle and never climb back
  // up, which is what separates a branch from a stray scribble.
  const anchors = [0.2, 0.36, 0.5, 0.63, 0.75, 0.86];
  for (const t of anchors) {
    const i = Math.round(t * (trunk.length - 1));
    const a = trunk[i]!;
    const b = trunk[Math.min(i + 3, trunk.length - 1)]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ang = (rand() < 0.5 ? -1 : 1) * (0.42 + rand() * 0.5);
    const reach = (BOLT_H - a.y) * (0.24 + rand() * 0.34);
    const ux = (dx / len) * Math.cos(ang) - (dy / len) * Math.sin(ang);
    const uy = (dx / len) * Math.sin(ang) + (dy / len) * Math.cos(ang);
    const tip = {
      x: clamp(a.x + ux * reach, 8, BOLT_W - 8),
      y: clamp(a.y + uy * reach, a.y + 24, BOLT_H - 4),
    };
    const branch = roughen([a, tip], rand, 0.22, 5, 4);
    const w = 0.5 - t * 0.22;
    strokes.push({ d: toPath(branch), w });

    // Every other branch forks once more. Branching that stops after one level
    // looks drawn; branching that repeats looks grown.
    if (rand() < 0.55) {
      const j = Math.round(branch.length * (0.4 + rand() * 0.3));
      const c = branch[Math.min(j, branch.length - 1)]!;
      const ang2 = (rand() < 0.5 ? -1 : 1) * (0.5 + rand() * 0.5);
      const reach2 = (BOLT_H - c.y) * (0.16 + rand() * 0.22);
      const vx = (tip.x - a.x) / (Math.hypot(tip.x - a.x, tip.y - a.y) || 1);
      const vy = (tip.y - a.y) / (Math.hypot(tip.x - a.x, tip.y - a.y) || 1);
      const wx = vx * Math.cos(ang2) - vy * Math.sin(ang2);
      const wy = vx * Math.sin(ang2) + vy * Math.cos(ang2);
      const tip2 = {
        x: clamp(c.x + wx * reach2, 6, BOLT_W - 6),
        y: clamp(c.y + wy * reach2, c.y + 14, BOLT_H - 2),
      };
      strokes.push({ d: toPath(roughen([c, tip2], rand, 0.24, 4, 3)), w: w * 0.6 });
    }
  }

  return strokes;
}

/**
 * The bolt as an SVG data URI, ready for `mask-image`. A data URI cannot read a
 * CSS variable, so this paints WHITE and is used as a mask over a coloured
 * layer - the same arrangement as the honeycomb and circuit effects.
 *
 * `base` is the trunk's absolute stroke width in the 300 x 500 space. Calling
 * this twice with a thin and a wide base gives the white hot channel and its
 * halo from one geometry, perfectly aligned because they ARE one geometry.
 */
export function boltMaskUrl(strokes: BoltStroke[], base: number): string {
  const paths = strokes
    .map((s) => `%3Cpath d='${s.d}' stroke-width='${round1(Math.max(0.4, s.w * base))}'/%3E`)
    .join("");
  return (
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${BOLT_W}' height='${BOLT_H}' ` +
    `viewBox='0 0 ${BOLT_W} ${BOLT_H}'%3E%3Cg fill='none' stroke='%23ffffff' stroke-linecap='round' ` +
    `stroke-linejoin='round'%3E${paths}%3C/g%3E%3C/svg%3E")`
  );
}
